import React from 'react';
import { StanButton, StanTag } from '@/components';
import { SUBSCRIPTION_STATUS } from '@/lib/api/subscriptions/types';
import { PAYMENT_DISCOUNTS } from '@/lib';

interface SubscriptionSectionProps {
    currentSubscriptionStatus: number;
    currentBilledPlan: string;
    pricingDetail: string;
    billingCycle: string;
    nextBillDate: string | null | undefined;
    platformSubscription:
        | {
              status?: number;
              valid_until?: number | null;
              data?: {
                  subscription_schedule?: {
                      start_date: string;
                      subscription_tier: string;
                      price?: {
                          interval?: string;
                      };
                  };
              };
          }
        | null
        | undefined;
    loading: boolean;
    isAffiliateOnly?: boolean;
    isNoExtraCostTrial?: boolean;
    trialEndDate?: number;
    trialExpirationMessage?: string | null;
    activeCouponName?: string;
    isFreeForever?: boolean;
    /** Whether user is in a free trial (no_cc_trial experiment) with no payment method */
    isInFreeTrial?: boolean;
    onCancelSubscription?: () => void;
    onChangePlan?: () => void;
    onRenewSubscription?: () => void;
}

export const SubscriptionSection: React.FC<SubscriptionSectionProps> = ({
    currentSubscriptionStatus,
    currentBilledPlan,
    pricingDetail,
    billingCycle,
    nextBillDate,
    platformSubscription,
    loading,
    isAffiliateOnly = false,
    isNoExtraCostTrial = false,
    trialEndDate,
    trialExpirationMessage,
    activeCouponName,
    isFreeForever = false,
    isInFreeTrial = false,
    onCancelSubscription,
    onChangePlan,
    onRenewSubscription,
}) => {
    const showCancelSubscriptionAction =
        platformSubscription?.status === SUBSCRIPTION_STATUS.ACTIVE ||
        platformSubscription?.status === SUBSCRIPTION_STATUS.DOWNGRADED;

    // Don't show renew button if user is in free trial (no_cc_trial experiment)
    // with no payment method and remaining trial days
    const showRenewSubscriptionAction =
        platformSubscription?.status === SUBSCRIPTION_STATUS.CANCELLED && !isInFreeTrial;

    // Build downgrade message
    const downgradeMessage =
        currentSubscriptionStatus === SUBSCRIPTION_STATUS.DOWNGRADED &&
        platformSubscription?.data?.subscription_schedule
            ? (() => {
                  const schedule = platformSubscription.data.subscription_schedule;
                  const changeDate = new Date(schedule.start_date);
                  const formattedDate = changeDate.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                  });
                  return `Your subscription will change to ${schedule.subscription_tier} ${schedule.price?.interval}ly on ${formattedDate}`;
              })()
            : null;

    // Calculate time left in trial
    const timeLeftInTrial =
        isNoExtraCostTrial && trialEndDate
            ? (() => {
                  const now = Date.now();
                  const end = trialEndDate * 1000;
                  const diff = end - now;
                  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

                  if (days > 0) return `${days} day${days !== 1 ? 's' : ''} left`;
                  if (hours > 0) return `${hours} hour${hours !== 1 ? 's' : ''} left`;
                  return 'Ending soon';
              })()
            : null;

    return (
        <div className='space-y-6'>
            <h4>Your Stan Subscription</h4>

            {/* No Extra Cost Trial Card */}
            {isNoExtraCostTrial && (
                <div className='bg-bg-subtle rounded-lg p-4'>
                    <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                            <h4>
                                Trialing <strong>Creator Pro</strong>
                            </h4>
                        </div>
                        {timeLeftInTrial && (
                            <span className='bg-brand-subtle text-brand-main rounded-full px-3 py-1 text-sm font-bold'>
                                {timeLeftInTrial}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {currentSubscriptionStatus >= SUBSCRIPTION_STATUS.CANCELLED ? (
                <div className='bg-bg-subtle space-y-4 rounded-lg p-4'>
                    <div className='flex items-center justify-between'>
                        <h4 className='font-semibold capitalize'>{currentBilledPlan}</h4>
                        {pricingDetail && <p className='font-semibold'>{pricingDetail}</p>}
                    </div>

                    <div className='flex items-center justify-between'>
                        <p
                            className={`${currentSubscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED ? 'text-negative-fg-negative-bold' : currentSubscriptionStatus === SUBSCRIPTION_STATUS.PENDING_ASSISTANCE ? 'text-brand-main' : 'text-gray'}`}
                        >
                            {billingCycle}
                        </p>
                        {!isFreeForever && activeCouponName && (
                            <StanTag label={activeCouponName} className='bg-brand-subtle text-brand-main font-bold' />
                        )}
                    </div>

                    {isFreeForever && <p className=''>{PAYMENT_DISCOUNTS.FREE_FOREVER}</p>}

                    {!isFreeForever && (
                        <>
                            {currentSubscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED &&
                                platformSubscription?.valid_until && (
                                    <p className='text-gray'>
                                        Your subscription will end on{' '}
                                        {new Date(platformSubscription.valid_until * 1000).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        })}
                                    </p>
                                )}

                            {currentSubscriptionStatus === SUBSCRIPTION_STATUS.DOWNGRADED && downgradeMessage && (
                                <p className='text-destructive'>{downgradeMessage}</p>
                            )}

                            {currentSubscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE && nextBillDate && (
                                <p className=''>{nextBillDate}</p>
                            )}

                            {trialExpirationMessage && <p className='text-gray'>{trialExpirationMessage}</p>}
                        </>
                    )}
                </div>
            ) : (
                <div className='bg-negative-subtle rounded-lg p-4'>
                    <h4 className='text-body-1-regular font-semibold'>No valid subscription found</h4>
                </div>
            )}

            {/* Action Buttons - Hidden for affiliate users */}
            {!isAffiliateOnly && currentSubscriptionStatus >= SUBSCRIPTION_STATUS.CANCELLED && (
                <div className='flex flex-wrap gap-4'>
                    {showCancelSubscriptionAction && (
                        <StanButton
                            name='cancel-subscription-button'
                            variant='outline'
                            color='destructive'
                            disabled={loading}
                            size='sm'
                            onClick={onCancelSubscription}
                        >
                            Cancel Subscription
                        </StanButton>
                    )}

                    <StanButton
                        name='change-plan-button'
                        size='sm'
                        variant='outline'
                        disabled={loading}
                        onClick={onChangePlan}
                    >
                        Change Plan
                    </StanButton>

                    {showRenewSubscriptionAction && (
                        <StanButton
                            name='renew-subscription-button'
                            disabled={loading}
                            size='sm'
                            loading={loading}
                            onClick={onRenewSubscription}
                        >
                            Renew Subscription
                        </StanButton>
                    )}
                </div>
            )}
        </div>
    );
};
